import React from 'react';
import type { CrudConfig } from '@/components/shared/crud-page';
import { extractImageUrl } from '@/components/shared/crud-page';
import { EditChecklistLink } from './edit-checklist-link';
import { EMPLOYEE_REF_QUERY, ASSET_REF_QUERY } from '@/lib/ref-defaults';
import { DispatchJobProductsSubform, persistStagedDispatchProducts } from './dispatch-job-products-subform';
import { JobCardMaterialsSubform, persistStagedJobMaterials } from './job-card-materials-subform';
import { ServicePlanScheduleSubform, persistStagedServicePlanSchedules } from './service-plan-schedules-subform';
import { ServiceHistoryDocumentsSubform } from './service-history-documents-subform';
import { AssetFilesSubform, FileDownloadLink } from './asset-files-subform';
import { triggerAssetSync, canSyncAssetFrom } from './trigger-asset-sync';
import { AssetOffboardDialog } from './asset-offboard-dialog';
import { AssetOffboardPickerDialog } from './asset-offboard-picker-dialog';
import { fmtDate, fmtDateTime } from '@/lib/format-date';
import { SCHEDULED } from '@/pages/my-dispatch/status';
import { CustomerPicker, customerPickerError } from '@/components/shared/customer-picker';
// `asset-service-plans-picker.ts` is intentionally not imported anymore —
// the picker is now a plain `servicePlan` ref field on the form, and
// persistence is handled natively by the Asset model + sync hook. The
// helper file is left in place in case future work brings back a
// cross-collection editor; we can prune it once Vendor / Customer also
// migrate off populated-array patterns.

// Re-export so other configs that import from this file keep working.
export { EMPLOYEE_REF_QUERY };

/** Short suffix for a ServicePlanSchedule.unitOfMeasurement value, so a
 *  next-service target reads "320,000 km" rather than "320,000 Distance (km)".
 *  Falls through to the raw value for anything unrecognised. */
export function unitSuffix(unit: string | null | undefined): string {
  const u = String(unit ?? '').toLowerCase();
  if (u.includes('km') || u.includes('distance')) return 'km';
  if (u.includes('hour')) return 'hrs';
  if (u.includes('month')) return 'months';
  if (u.includes('day')) return 'days';
  return String(unit ?? '');
}

// Shared label map for JobCard.status — keep in sync with server enum.
// (Previously named WORKFLOW_STATUS_*; merged from `workflowStatus` into
// the unified `status` field, but kept old export names available below
// as aliases to spare downstream importers from churn.)
export const JOB_CARD_STATUS_LABELS: Record<string, string> = {
  active:    'Active',
  completed: 'Completed',
  approved:  'Approved',
  voided:    'Voided',
};
export const jobCardStatusLabel = (s?: string | null) =>
  (s && JOB_CARD_STATUS_LABELS[s]) || s || 'Active';
/** @deprecated Use `JOB_CARD_STATUS_LABELS` / `jobCardStatusLabel`. */
export const WORKFLOW_STATUS_LABELS = JOB_CARD_STATUS_LABELS;
/** @deprecated Use `jobCardStatusLabel`. */
export const workflowStatusLabel = jobCardStatusLabel;


/** Compact elapsed time between two ISO timestamps for table cells.
 *  Returns "—" when either side is missing (driver skipped a status hop). */
function fmtElapsedAdmin(fromIso?: string | null, toIso?: string | null): string {
  if (!fromIso || !toIso) return '—';
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Primary tabs shared by every asset bucket — Active / Archived / Disposed
// sit on separate /fleet/* endpoints each with their own baseFilter;
// Contractor Vehicles lives in fleet-extras under a different permission
// slug. Cross-route navigation via `to:` makes them feel like one report
// with tabs.
const ASSET_LIFECYCLE_TABS = [
  { label: 'Active',              to: '/fleet/assets' },
  { label: 'Archived',            to: '/fleet/archived-assets' },
  { label: 'Disposed',            to: '/fleet/disposed-assets' },
  { label: 'Contractor Vehicles', to: '/fleet/contractor-vehicles' },
];
// Secondary tabs — IoT tracking provider. Independent of the lifecycle
// row above, so an operator can pick e.g. "Archived" × "EROAD" to find
// the EROAD-tagged archived assets. Filter param survives lifecycle tab
// switches because `selectFromTabs` preserves keys owned by the other row.
// Coretex was retired — kept off this row plus filtered out of the IoT
// suppliers master-data list so legacy data still reads but no new
// records can be tagged with it.
const ASSET_IOT_TABS: { label: string; filter?: Record<string, string> }[] = [
  { label: 'All' },
  // EROAD / Blackhawk are tracking-feed providers. Komatsu-equipment machines
  // are surfaced under their own Komtrax tab even when their telemetry is fed
  // via Blackhawk — so exclude the Komatsu device here to keep the IoT tabs
  // mutually exclusive (no double-counting). The provider field itself is left
  // as-is (still 'Blackhawk' for those machines) so the Blackhawk data sync,
  // which keys off the provider, keeps updating them.
  { label: 'EROAD',     filter: { trackingProvider: 'EROAD', deviceType_ne: 'KOMATSU - EQUIPMENT' } },
  { label: 'Blackhawk', filter: { trackingProvider: 'Blackhawk', deviceType_ne: 'KOMATSU - EQUIPMENT' } },
  // Komtrax = Komatsu's telematics. Identified by the Komatsu device, NOT the
  // tracking provider (data feeds in via Blackhawk or a direct Komtrax
  // provider, which varies per machine). Keying on deviceType captures them all.
  { label: 'Komtrax',   filter: { deviceType: 'KOMATSU - EQUIPMENT' } },
];

export const FLEET_CONFIGS: Record<string, CrudConfig> = {
  assets: {
    title: 'Assets', singular: 'Asset', endpoint: '/fleet/assets', formSlug: 'assets', pageSlug: 'assets_page',
    // Two-axis tabs:
    //   Primary  — lifecycle (Active / Archived / Disposed / Contractor)
    //   Secondary — IoT provider (All / EROAD / Blackhawk / Coretex)
    // Independent navigation so the operator can pivot on either axis.
    tabs: ASSET_LIFECYCLE_TABS,
    secondaryTabs: ASSET_IOT_TABS,
    toolbarActions: [
      {
        label: 'Sync EROAD',
        actionKey: 'asset.sync.eroad',
        onClick: async ({ refetch, navigate }) => {
          await triggerAssetSync('eroad', navigate);
          refetch();
        },
      },
      {
        label: 'Sync Blackhawk',
        actionKey: 'asset.sync.blackhawk',
        onClick: async ({ refetch, navigate }) => {
          await triggerAssetSync('blackhawk', navigate);
          refetch();
        },
      },
    ],
    // Bulk lifecycle actions — `actionKey` gates visibility against the
    // user's role (the CrudPage filters bulkActions where the key isn't in
    // their granted actions list). Server-side enforcement comes from the
    // matching `requireAction` middleware on each endpoint.
    bulkActions: [
      // Targeted sync of just the selected vehicles. One entry per provider so
      // each keeps its own permission; `enabledFor` hides it when no selected
      // row is linked to that provider, and the server skips the rows that
      // aren't.
      { label: 'Sync from EROAD',
        actionKey: 'asset.sync.eroad',
        enabledFor: (r) => canSyncAssetFrom('eroad', r),
        onClick: async (ids, { refetch, navigate, clearSelection }) => {
          clearSelection();
          await triggerAssetSync('eroad', navigate, ids);
          refetch();
        } },
      { label: 'Sync from Blackhawk',
        actionKey: 'asset.sync.blackhawk',
        enabledFor: (r) => canSyncAssetFrom('blackhawk', r),
        onClick: async (ids, { refetch, navigate, clearSelection }) => {
          clearSelection();
          await triggerAssetSync('blackhawk', navigate, ids);
          refetch();
        } },
      { label: 'NZTA Update',
        actionKey: 'asset.bulk-nzta-update',
        endpoint: '/fleet/assets/bulk-nzta-update',
        confirm: 'Mark all selected assets as NZTA registered?' },
      { label: 'Recompute Service Report',
        actionKey: 'asset.bulk-recompute-services',
        endpoint: '/fleet/assets/bulk-recompute-services',
        confirm: 'Re-run the Service Report computation for the selected assets?' },
      { label: 'Archive',
        actionKey: 'asset.bulk-archive',
        endpoint: '/fleet/assets/bulk-archive',
        confirm: 'Archive all selected assets? They will be hidden from the active fleet.' },
      { label: 'Dispose',
        actionKey: 'asset.bulk-dispose',
        endpoint: '/fleet/assets/bulk-dispose',
        confirm: 'Dispose all selected assets? They will be removed from the active fleet.',
        variant: 'destructive' },
    ],
    // Per-row offboard form — opens a dialog that lets the operator pick
    // the outcome (Archived / Disposed), supply Reason + Additional Info,
    // and notify the IoT provider via the configured offboard email
    // template. Hidden once the asset is already off the active fleet.
    rowActions: [
      // Sync just this vehicle — shows in the right-click menu. Only offered
      // for the provider the asset is linked to.
      {
        label: 'Sync from EROAD',
        actionKey: 'asset.sync.eroad',
        visible: (r) => canSyncAssetFrom('eroad', r),
        onClick: async (r, { refetch, navigate }) => {
          await triggerAssetSync('eroad', navigate, [String(r.id)]);
          refetch();
        },
      },
      {
        label: 'Sync from Blackhawk',
        actionKey: 'asset.sync.blackhawk',
        visible: (r) => canSyncAssetFrom('blackhawk', r),
        onClick: async (r, { refetch, navigate }) => {
          await triggerAssetSync('blackhawk', navigate, [String(r.id)]);
          refetch();
        },
      },
      {
        label: 'Offboard...',
        actionKey: 'asset.offboard',
        customDialog: AssetOffboardDialog,
        visible: (r) => r.vehicleStatus !== 'Archived' && r.vehicleStatus !== 'Disposed',
      },
    ],
    // Full Zoho-style report catalogue. All columns are visible by default;
    // users can hide what they don't want (saved per-user via UserPreference).
    columns: [
      // Photo — prefer the S3-signed `photoUrl`; fall back to a URL
      // extracted from the legacy Zoho `photo` HTML for assets that
      // haven't been migrated to S3 yet.
      { key: 'photoUrl', label: 'Photo', exportFormat: 'image',
        render: (r) => {
          const src = r.photoUrl ?? extractImageUrl(r.photo);
          return src
            ? React.createElement('img', { src, alt: r.fleetNumber ?? '', className: 'h-20 w-20 object-cover rounded' })
            : '—';
        } },
      { key: 'fleetNumber', label: 'Fleet #', filterType: 'text', filterMulti: true,
        // fleetNumber is free-text (legacy "573- Bobcat S130" exists), so a
        // string sort would put "10" before "9". The model auto-derives a
        // numeric prefix into `fleetNumberSort` for natural ordering.
        // `filterMulti: true` lets the user enter "123", press Enter,
        // enter "213", press Enter — and the report ORs across both.
        sortField: 'fleetNumberSort' },
      { key: 'registrationNumber', label: 'Rego #', filterType: 'text' },
      { key: 'vehicleStatus', label: 'Vehicle Status', filterType: 'enum',
        enumOptions: [
          { value: 'Active', label: 'Active' },
          { value: 'Available', label: 'Available' },
          { value: 'On Job', label: 'On Job' },
          { value: 'Archived', label: 'Archived' },
          { value: 'Disposed', label: 'Disposed' },
        ] },
      { key: 'serviceStatus', label: 'Service Status', filterType: 'text' },
      { key: 'status', label: 'Lifecycle', filterType: 'text' },
      { key: 'make', label: 'Make', filterType: 'text' },
      { key: 'model', label: 'Model', filterType: 'text' },
      { key: 'year', label: 'Year', filterType: 'number' },
      { key: 'colour', label: 'Colour', filterType: 'text' },
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'vehicleType.type', label: 'Vehicle Type',
        filterType: 'text', filterField: 'vehicleType',
        render: (r) => r.vehicleType?.type || '—' },
      { key: 'vehicleGroup.group', label: 'Vehicle Group',
        filterType: 'text', filterField: 'vehicleGroup',
        render: (r) => r.vehicleGroup?.group || '—' },
      { key: 'company.name', label: 'Company',
        filterType: 'text', filterField: 'company',
        render: (r) => r.company?.name || '—' },
      { key: 'department.name', label: 'Department',
        filterType: 'text', filterField: 'department',
        render: (r) => r.department?.name || '—' },
      { key: 'branch.name', label: 'Branch',
        filterType: 'text', filterField: 'branch',
        render: (r) => r.branch?.name || '—' },
      { key: 'depot.code', label: 'Depot Code',
        filterType: 'text', filterField: 'depot',
        render: (r) => r.depot?.code || '—' },
      { key: 'depot.description', label: 'Depot Description',
        render: (r) => r.depot?.description || '—' },
      { key: 'serviceLocation.name', label: 'Service Location',
        filterType: 'ref', filterField: 'serviceLocation',
        refEndpoint: '/master-data/service-locations', refLabel: 'name',
        render: (r) => r.serviceLocation?.name || '—' },
      { key: 'notes', label: 'Notes', filterType: 'text' },
      { key: 'nztaRegistered', label: 'NZTA Registered', filterType: 'boolean',
        render: (r) => r.nztaRegistered === true ? 'Yes' : r.nztaRegistered === false ? 'No' : '—' },
      { key: 'regoExpiry', label: 'Rego Expiry', filterType: 'date',
        render: (r) => fmtDate(r.regoExpiry) },
      { key: 'wofExpiry', label: 'WoF Expiry', filterType: 'date',
        render: (r) => fmtDate(r.wofExpiry) },
      { key: 'cofExpiry', label: 'CoF Expiry', filterType: 'date',
        render: (r) => fmtDate(r.cofExpiry) },
      { key: 'currentOdo', label: 'Odo', filterType: 'number',
        render: (r) => typeof r.currentOdo === 'number' ? r.currentOdo.toLocaleString() : '—' },
      { key: 'currentHours', label: 'Hours', filterType: 'number',
        render: (r) => typeof r.currentHours === 'number' ? r.currentHours.toLocaleString() : '—' },
      { key: 'currentHubo', label: 'Hubo', filterType: 'number',
        render: (r) => typeof r.currentHubo === 'number' ? r.currentHubo.toLocaleString() : '—' },
      { key: 'online', label: 'Online', filterType: 'boolean',
        render: (r) => r.online ? 'Yes' : 'No' },
      { key: 'lastSeenAt', label: 'Last Online', filterType: 'date', exportFormat: 'datetime',
        render: (r) => fmtDateTime(r.lastSeenAt) },
      { key: 'updatedAt', label: 'Last Updated', filterType: 'date', exportFormat: 'datetime',
        render: (r) => fmtDateTime(r.updatedAt) },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
      { key: 'trackingProvider', label: 'Provider', filterType: 'enum',
        enumOptions: [
          { value: 'EROAD', label: 'EROAD' },
          { value: 'Blackhawk', label: 'Blackhawk' },
        ] },
      { key: 'deviceType', label: 'Device Type', filterType: 'text' },
      // Service Plan column — populated by the route's `assetEnrichment`
      // afterReadAll hook (the relationship lives on ServicePlan.assignedAssets,
      // not on Asset itself). Server's `defaultFilter` translates the
      // `?servicePlan=<id>` query into an `_id: { $in: [...] }` against
      // that plan's assignedAssets, so the column's ref filter just works.
      { key: 'servicePlan.name', label: 'Service Plan',
        filterType: 'ref', filterField: 'servicePlan',
        refEndpoint: '/servicing/plans', refLabel: 'name',
        render: (r) => r.servicePlan?.name || '—' },
      { key: 'lastScheduledService.date', label: 'Last Service', exportFormat: 'date',
        render: (r) => r.lastScheduledService?.date
          ? fmtDate(r.lastScheduledService.date)
          : '—' },
      { key: 'serialNumber', label: 'Serial #', filterType: 'text' },
      { key: 'chassisNumber', label: 'Chassis #', filterType: 'text' },
      { key: 'engineNumber', label: 'Engine #', filterType: 'text' },
      // `updatedAt` is already declared above as "Last Updated" — second
      // declaration removed (it caused a React duplicate-key warning on
      // the Assets table).
      { key: 'assetType', label: 'Asset Type', filterType: 'enum',
        enumOptions: [
          { value: 'Atlas', label: 'Atlas' },
          { value: 'Contractor', label: 'Contractor' },
        ] },
    ],
    fields: [
      // Basic Info
      { section: 'Basic Info', name: 'photoKey', label: 'Photo', type: 'image', uploadPrefix: 'asset',
        // Surface the legacy Zoho `photo` HTML field as a fallback preview
        // when this asset's photo hasn't been migrated to S3 yet.
        legacyPreviewField: 'photo' },
      { section: 'Basic Info', name: 'fleetNumber', label: 'Fleet Number', type: 'text', required: true },
      { section: 'Basic Info', name: 'assetType', label: 'Asset Type', type: 'select', options: [
        { value: 'Atlas', label: 'Atlas' },
        { value: 'Contractor', label: 'Contractor' },
      ] },
      { section: 'Basic Info', name: 'registrationNumber', label: 'Registration', type: 'text' },
      { section: 'Basic Info', name: 'description', label: 'Description', type: 'text' },
      { section: 'Basic Info', name: 'make', label: 'Make', type: 'text' },
      { section: 'Basic Info', name: 'model', label: 'Model', type: 'text' },
      { section: 'Basic Info', name: 'year', label: 'Year', type: 'number' },
      { section: 'Basic Info', name: 'colour', label: 'Colour', type: 'text' },

      // Categorization
      { section: 'Categorization', name: 'company', label: 'Company', type: 'ref', refEndpoint: '/master-data/companies', refLabel: 'name' },
      { section: 'Categorization', name: 'department', label: 'Department', type: 'ref', refEndpoint: '/master-data/departments', refLabel: 'name' },
      { section: 'Categorization', name: 'branch', label: 'Branch', type: 'ref', refEndpoint: '/master-data/branches', refLabel: 'name' },
      { section: 'Categorization', name: 'depot', label: 'Depot', type: 'ref', refEndpoint: '/master-data/depots', refLabel: 'code' },
      { section: 'Categorization', name: 'serviceLocation', label: 'Service Location', type: 'ref',
        refEndpoint: '/master-data/service-locations', refLabel: 'name' },
      { section: 'Categorization', name: 'site', label: 'Site', type: 'ref', refEndpoint: '/master-data/sites', refLabel: 'name' },
      { section: 'Categorization', name: 'vehicleType', label: 'Vehicle Type', type: 'ref', refEndpoint: '/master-data/vehicle-types', refLabel: 'type' },
      { section: 'Categorization', name: 'vehicleGroup', label: 'Vehicle Group', type: 'ref', refEndpoint: '/master-data/vehicle-groups', refLabel: 'group' },
      { section: 'Categorization', name: 'dispatchGroup', label: 'Dispatch Group', type: 'ref',
        refEndpoint: '/master-data/dispatch-groups', refLabel: 'name' },
      // The Service Plan this asset is on. Lives on `ServicePlan.assignedAssets[]`
      // on the server side — the asset CRUD's `afterReadOne` attaches the
      // derived value so this picker can show the current plan, and the
      // config's `afterSave` writes any change back to that array.
      { section: 'Categorization', name: 'servicePlan', label: 'Service Plan', type: 'ref',
        refEndpoint: '/servicing/plans', refLabel: 'name' },
      // Toggle whether this asset appears as a row on the Dispatch Schedule.
      // Defaults to true on the model so legacy / freshly-imported assets show
      // up automatically; flip off for assets that shouldn't be dispatchable
      // (e.g. trailers, spares, tooling kept on the fleet for compliance).
      { section: 'Categorization', name: 'showInDispatch', label: 'Display in Scheduler', type: 'boolean' },

      // Specifications
      { section: 'Specifications', name: 'chassisNumber', label: 'Chassis Number', type: 'text' },
      { section: 'Specifications', name: 'engineNumber', label: 'Engine Number', type: 'text' },
      { section: 'Specifications', name: 'serialNumber', label: 'Serial Number', type: 'text' },
      { section: 'Specifications', name: 'fuelType', label: 'Fuel Type', type: 'select', options: [
        { value: 'Diesel', label: 'Diesel' },
        { value: 'Petrol', label: 'Petrol' },
        { value: 'Electric', label: 'Electric' },
      ] },
      { section: 'Specifications', name: 'fuelMeasurementIn', label: 'Fuel Measurement In', type: 'select', options: [
        { value: 'Liters', label: 'Liters' },
        { value: 'Gallons (US)', label: 'Gallons (US)' },
        { value: 'Gallons (UK)', label: 'Gallons (UK)' },
      ] },
      { section: 'Specifications', name: 'grossMassWeight', label: 'Gross Mass Weight (kg)', type: 'number' },
      { section: 'Specifications', name: 'grossUnladenWeight', label: 'Gross Unladen Weight (kg)', type: 'number' },

      // Registration
      { section: 'Registration', name: 'nztaRegistered', label: 'NZTA Registered', type: 'boolean' },
      { section: 'Registration', name: 'nztaType', label: 'NZTA Type', type: 'textarea' },
      { section: 'Registration', name: 'subjectToRuc', label: 'Subject to RUC', type: 'boolean' },
      // Expiry dates — operators need to set these on the form (the NZTA
      // bulk-update action populates them too, but a manual override path
      // is still required for assets that bypass that flow). Columns at
      // lines 203-206 already display the values; without these field
      // declarations the edit form had no input for them.
      { section: 'Registration', name: 'regoExpiry', label: 'Rego Expiry', type: 'date' },
      { section: 'Registration', name: 'wofExpiry', label: 'WoF Expiry', type: 'date' },
      { section: 'Registration', name: 'cofExpiry', label: 'CoF Expiry', type: 'date' },

      // Tracking & IoT
      // Provider list comes from the IoT Suppliers master-data form
      // (Master Data → IoT Suppliers). The picked supplier's name is
      // stored on the asset as a plain string (existing column shape,
      // no migration), so `refValueField: 'providerName'` makes the
      // LookupField persist the name instead of an ObjectId. Add a new
      // row in IoT Suppliers and it appears here automatically.
      { section: 'Tracking & IoT', name: 'trackingProvider', label: 'Tracking Provider', type: 'ref',
        refEndpoint: '/master-data/iot-suppliers', refLabel: 'providerName', refValueField: 'providerName' },
      { section: 'Tracking & IoT', name: 'deviceType', label: 'Device Type', type: 'text' },
      { section: 'Tracking & IoT', name: 'deviceSerialNumber', label: 'Device Serial Number', type: 'text' },
      // GPS / IoT identifiers are the join keys the tracking-sync service
      // uses to match this Asset to a vehicle in the provider's system.
      // A wrong value cross-links the asset (no telemetry, or someone
      // else's). Locked for non-admin users — onboarding sets them once,
      // and an Administrator can still correct mistakes.
      { section: 'Tracking & IoT', name: 'gpsId', label: 'GPS ID', type: 'text',
        unlockAction: 'asset.edit-device-ids' },
      // Provider-specific identifiers — only the row matching the chosen
      // Tracking Provider is shown so the form doesn't ask for an EROAD ID
      // when the operator picked Blackhawk. The fields stay in the config
      // (just hidden) so a previously-set value isn't wiped on save.
      { section: 'Tracking & IoT', name: 'eroadId', label: 'EROAD ID', type: 'text',
        unlockAction: 'asset.edit-device-ids',
        visibleIf: (v) => v.trackingProvider === 'EROAD' },
      { section: 'Tracking & IoT', name: 'blackhawkId', label: 'Blackhawk ID', type: 'text',
        unlockAction: 'asset.edit-device-ids',
        visibleIf: (v) => v.trackingProvider === 'Blackhawk' },

      // Usage Meters
      { section: 'Usage Meters', name: 'trackUsageAs', label: 'Track Usage As', type: 'select', options: [
        { value: 'Odometer', label: 'Odometer (km)' },
        { value: 'Hubometer', label: 'Hubometer (km)' },
        { value: 'Hours', label: 'Hours' },
      ] },
      { section: 'Usage Meters', name: 'currentOdo', label: 'Current Odo', type: 'number' },
      { section: 'Usage Meters', name: 'currentHubo', label: 'Current Hubo', type: 'number' },
      { section: 'Usage Meters', name: 'currentHours', label: 'Current Hours', type: 'number' },
      { section: 'Usage Meters', name: 'secondaryAuxiliaryMeter', label: 'Secondary / Auxiliary Meter', type: 'boolean' },
      { section: 'Usage Meters', name: 'secondaryMeterUnit', label: 'Secondary Meter Unit', type: 'select', options: [
        { value: 'Kilometers', label: 'Kilometers' },
        { value: 'Miles', label: 'Miles' },
        { value: 'Hours', label: 'Hours' },
      ] },

      // Status
      { section: 'Status', name: 'status', label: 'Lifecycle Status', type: 'select', options: [
        { value: 'Active', label: 'Active' },
        { value: 'Purchased', label: 'Purchased' },
        { value: 'In Service', label: 'In Service' },
      ] },
      { section: 'Status', name: 'vehicleStatus', label: 'Vehicle Status', type: 'select', options: [
        { value: 'Active', label: 'Active' },
        { value: 'Archived', label: 'Archived' },
        { value: 'Disposed', label: 'Disposed' },
      ] },

      // Notes
      { section: 'Notes', name: 'notes', label: 'Notes', type: 'textarea' },
    ],
    // `servicePlan` is now a denormalised field on the Asset model itself
    // (kept in sync with ServicePlan.assignedAssets by a model hook). The
    // standard save path persists it natively — no `afterSave` needed.
    relatedLists: [
      // Order matches the order the user expects on the Asset detail view —
      // recent activity / audit at the top, operational logs below, then
      // forward-looking and reference lists. AuditLog is shared across entity
      // types so it needs the `entityType` filterExtra alongside entityId.
      { title: 'Change history', endpoint: '/audit-logs', filterField: 'entityId',
        filterExtra: { entityType: 'Asset' },
        columns: [
          { key: 'createdAt', label: 'When', render: (r) => fmtDateTime(r.createdAt) },
          { key: 'operation', label: 'Op' },
          { key: 'changedBy.fullName', label: 'By', render: (r) => r.changedBy?.fullName || r.source },
          { key: 'changes', label: 'Changes', render: (r) => (r.changes || []).map((c: any) => c.field).join(', ') || '—' },
        ] },
      { title: 'Licence history', endpoint: '/license-renewals', filterField: 'asset',
        columns: [
          { key: 'dateOfIssue', label: 'Issued', render: (r) => fmtDate(r.dateOfIssue) },
          { key: 'licencePeriod', label: 'Period (mo)' },
          { key: 'purchaseAmount', label: 'Cost' },
        ] },
      { title: 'WoF / CoF history', endpoint: '/wof-renewals', filterField: 'asset',
        columns: [
          { key: 'wofCof', label: 'Type' },
          { key: 'testingDate', label: 'Tested', render: (r) => fmtDate(r.testingDate) },
          { key: 'expiryDate', label: 'Expires', render: (r) => fmtDate(r.expiryDate) },
        ] },
      // "Last scheduled services" removed 2026-08-14. It read the
      // `lastscheduledserviceinfos` import — a snapshot of a Zoho form that
      // churns (records deleted and recreated with new ids), and whose rows
      // mostly never resolved a vehicle: 834 of prod's 955 rows have
      // `asset: null`, leaving only 24 of 349 active assets with any row at
      // all. 325 assets showed the section as "(0)", including ones with
      // dozens of services — fleet 318 showed 0 against 82 real scheduled
      // services. Importing more rows cannot fix it; the source decays.
      //
      // The "Service history" list below covers the detail, and the Assets
      // report's "Last Service" column covers the summary (both now derived
      // from ServiceHistory). The legacy data remains reachable through the
      // standalone "Last Scheduled Services" report in Extras.
      // Issues — hidden 2026-08-14, temporarily. The collection is empty on
      // prod (0 rows) and holds 5 blank test shells on dev, so the section
      // showed "(0)" on every asset. Nothing writes to it: the only server
      // reference is the generic CRUD route (extras.routes.ts:1086), no Zoho
      // data imported, and faults are recorded in practice on the service
      // record itself (ServiceHistory.faultDescriptions / descriptionOfWork).
      //
      // Kept rather than deleted because per-vehicle fault logging may still be
      // wanted — the model, route and standalone Issues report in Extras are
      // all untouched. To bring it back, uncomment; to make it useful it needs
      // enums on priority/status and a way to raise an issue from a job card.
      // { title: 'Issues', endpoint: '/issues', filterField: 'asset',
      //   columns: [
      //     { key: 'description', label: 'Description' },
      //     { key: 'priority', label: 'Priority' },
      //     { key: 'status', label: 'Status' },
      //   ] },
      // Comments — hidden 2026-08-14, temporarily, for the same reason as
      // Issues above. Prod has 0 rows; dev's 5 are blank shells with no asset,
      // no text and no zohoId, created 2026-05-06 in the same session as the
      // blank Issues rows. No Zoho data ever imported.
      //
      // More redundant than Issues: it models only (asset, free text), and
      // per-vehicle free text already exists as `Asset.notes` on the asset
      // form, with per-service notes on ServiceHistory.descriptionOfWork.
      // Model, route (extras.routes.ts:140) and data left untouched.
      // { title: 'Comments', endpoint: '/vehicle-comments', filterField: 'asset',
      //   columns: [{ key: 'comment', label: 'Comment' }] },
      { title: 'Files', endpoint: '/vehicle-files', filterField: 'asset',
        columns: [
          { key: 'fileName', label: 'File Name' },
          { key: 'fileUpload', label: 'Original Filename' },
          { key: 'createdAt', label: 'Uploaded', render: (r) => fmtDate(r.createdAt) },
          // Pre-signed S3 URL fetched on demand — opens the file in a new
          // tab. Click is stopPropagation'd inside FileDownloadLink so it
          // doesn't trigger the row-edit dialog the surrounding row uses.
          { key: '_download', label: 'Open',
            render: (r) => React.createElement(FileDownloadLink, { fileKey: r.fileKey }) },
        ],
        // Allow inline edit of the operator-supplied label without leaving
        // the detail view. Upload itself goes through the form's
        // AssetFilesSubform (multipart → S3 → metadata), but a quick rename
        // shouldn't need to re-open the asset edit dialog.
        rowEditFields: [
          { name: 'fileName', label: 'File Name', type: 'text' },
        ],
        rowEditSingular: 'File',
      },
      { title: 'Dispatch jobs', endpoint: '/dispatch/jobs', filterField: 'asset',
        columns: [
          { key: 'jobNumber', label: 'Job #' },
          { key: 'customer', label: 'Customer' },
          { key: 'status', label: 'Status' },
        ] },
      { title: 'Job cards', endpoint: '/fleet/job-cards', filterField: 'asset',
        columns: [
          { key: 'jobSheetNumber', label: 'Sheet #' },
          { key: 'jobDate', label: 'Job Date', render: (r) => fmtDate(r.jobDate) },
          { key: 'servicePlan.name', label: 'Service Plan', render: (r) => r.servicePlan?.name || '—' },
          { key: 'servicePlanSchedule.name', label: 'Schedule', render: (r) => r.servicePlanSchedule?.name || '—' },
          { key: 'submittedBy.fullName', label: 'Completed By', render: (r) => r.submittedBy?.fullName || '—' },
          { key: 'status', label: 'Status', render: (r) => jobCardStatusLabel(r.status) },
        ] },
      // Which schedule each row is for (A-Service, B-Service, …) — without it
      // an asset on a 4-schedule plan showed four indistinguishable rows.
      // Distance/hours-based schedules have a null `nextServiceDate` and carry
      // their target in `nextServiceValue`, so both are shown; matches the
      // columns on the full Upcoming Services report.
      { title: 'Upcoming services', endpoint: '/upcoming-services', filterField: 'asset',
        // Schedule order (A, B, C, D), not the endpoint's createdAt default —
        // which returned them as D, A, B, C on fleet 318. `numeric` keeps any
        // numbered schedules in human order (Service 2 before Service 10).
        sortBy: (a, b) => String(a.servicePlanSchedule?.name ?? '')
          .localeCompare(String(b.servicePlanSchedule?.name ?? ''), undefined, { numeric: true, sensitivity: 'base' }),
        columns: [
          { key: 'servicePlanSchedule.name', label: 'Schedule',
            render: (r) => r.servicePlanSchedule?.name || '—' },
          { key: 'nextServiceDate', label: 'Next Date', render: (r) => fmtDate(r.nextServiceDate) },
          { key: 'nextServiceValue', label: 'Next Value',
            render: (r) => (r.nextServiceValue != null
              ? `${Math.round(r.nextServiceValue).toLocaleString()}${r.unitOfMeasurement ? ` ${unitSuffix(r.unitOfMeasurement)}` : ''}`
              : '—') },
          { key: 'status', label: 'Status' },
        ] },
      { title: 'Service history', endpoint: '/servicing/history', filterField: 'asset',
        columns: [
          { key: 'jobSheetNumber', label: 'Job #' },
          { key: 'jobDate', label: 'Date', render: (r) => fmtDate(r.jobDate) },
          // `total` column dropped — Invoice Details fields were removed
          // from the Service History form, so new records won't have it
          // populated. Per-line costs live on the Labour/Material costs
          // related lists.
          { key: 'status', label: 'Status' },
        ] },
    ],
    // Inline "Asset Files" section in the edit form — uploads land in the
    // same /vehicle-files collection the detail panel's Files related list
    // reads from, so a file added here is immediately visible on the
    // detail view too.
    FormExtras: AssetFilesSubform,
    // New assets default to Active so the create form lands on the live-fleet
    // state (matches the contractor-vehicles form). Edits keep the saved value.
    defaultValues: { company: null, vehicleStatus: 'Active' },
  },
  'job-cards': {
    title: 'Job Cards', singular: 'Job Card', endpoint: '/fleet/job-cards', formSlug: 'job_cards', pageSlug: 'job_cards_page', ownerField: 'assignedTo',
    // Pre-filter tabs by workflow status. Order mirrors the lifecycle so
    // supervisors scan left-to-right (Active → Completed → terminal states).
    // "Completed" is the supervisor's daily review queue.
    tabs: [
      { label: 'All' },
      { label: 'Active',    filter: { status: 'active' } },
      { label: 'Completed', filter: { status: 'completed' } },
      { label: 'Approved',  filter: { status: 'approved' } },
      { label: 'Voided',    filter: { status: 'voided' } },
    ],
    // Aligned with Zoho `All_Job_Cards.json` / `Job_Card_List.json`. Some Zoho
    // columns are computed (counts of subform rows) — those need server-side
    // joins to surface; tracked as Tier-B follow-up.
    columns: [
      { key: 'jobSheetNumber', label: 'Job Sheet #', filterType: 'text' },
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        refEndpoint: '/fleet/assets', refLabel: 'fleetNumber',
        render: (r) => r.asset?.fleetNumber || '—' },
      { key: 'assignedTo', label: 'Assigned To',
        render: (r) => Array.isArray(r.assignedTo) && r.assignedTo.length > 0
          ? r.assignedTo.map((a: any) => a?.fullName || a?.name?.firstName || a?.id).filter(Boolean).join(', ')
          : '—' },
      // Unified status — merged from the legacy `workflowStatus` enum and the
      // free-text `status` field. Now the single source of truth.
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'active',    label: 'Active' },
          { value: 'completed', label: 'Completed' },
          { value: 'approved',  label: 'Approved' },
          { value: 'voided',    label: 'Voided' },
        ],
        render: (r) => jobCardStatusLabel(r.status) },
      { key: 'servicePlan.name', label: 'Service Plan',
        filterType: 'text', filterField: 'servicePlan',
        refEndpoint: '/servicing/plans', refLabel: 'name',
        render: (r) => r.servicePlan?.name || '—' },
      { key: 'servicePlanSchedule.name', label: 'Schedule',
        filterType: 'text', filterField: 'servicePlanSchedule',
        refEndpoint: '/servicing/schedules', refLabel: 'name',
        render: (r) => r.servicePlanSchedule?.name || '—' },
      { key: 'serviceLocation.name', label: 'Service Location',
        filterType: 'ref', filterField: 'serviceLocation',
        refEndpoint: '/master-data/service-locations', refLabel: 'name',
        render: (r) => r.serviceLocation?.name || '—' },
      // Zoho parity — static multi-select of classifications. Comma-
      // joined for the report cell; full chip list lives on the form.
      { key: 'jobType', label: 'Job Type', filterType: 'text',
        render: (r) => Array.isArray(r.jobType) && r.jobType.length > 0 ? r.jobType.join(', ') : '—' },
      { key: 'workDescription', label: 'Work Description', filterType: 'text' },
      { key: 'reviewNotes', label: 'Review Notes', filterType: 'text' },
      { key: 'totalTimeLogged', label: 'Hours', filterType: 'number',
        render: (r) => r.totalTimeLogged ?? '—' },
      { key: 'materialSummary.count', label: 'Materials',
        render: (r) => r.materialSummary?.count ?? 0 },
      { key: 'materialSummary.cost', label: 'Material Cost',
        render: (r) => typeof r.materialSummary?.cost === 'number'
          ? r.materialSummary.cost.toLocaleString('en-GB', { maximumFractionDigits: 2 })
          : '—' },
      { key: 'jobDate', label: 'Job Date', filterType: 'date',
        render: (r) => fmtDate(r.jobDate) },
      { key: 'startDate', label: 'Start', filterType: 'date',
        render: (r) => fmtDateTime(r.startDate) },
      { key: 'endDate', label: 'End', filterType: 'date',
        render: (r) => fmtDateTime(r.endDate) },
      { key: 'submittedBy.fullName', label: 'Submitted By',
        filterType: 'text', filterField: 'submittedBy',
        refEndpoint: '/employees', refLabel: 'fullName',
        render: (r) => r.submittedBy?.fullName || '—' },
      { key: 'submittedAt', label: 'Submitted', filterType: 'date',
        render: (r) => fmtDateTime(r.submittedAt) },
      { key: 'reviewedBy.fullName', label: 'Reviewed By',
        filterType: 'text', filterField: 'reviewedBy',
        refEndpoint: '/employees', refLabel: 'fullName',
        render: (r) => r.reviewedBy?.fullName || '—' },
      { key: 'reviewedAt', label: 'Reviewed', filterType: 'date',
        render: (r) => fmtDateTime(r.reviewedAt) },
      { key: 'completedTime', label: 'Completed', filterType: 'date',
        render: (r) => fmtDateTime(r.completedTime) },
      { key: 'servicedWith', label: 'Serviced With', filterType: 'text' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
      { key: 'updatedAt', label: 'Updated', filterType: 'date',
        render: (r) => fmtDateTime(r.updatedAt) },
    ],
    rowActions: [
      // Both hidden for vendor jobs: starting a service opens a TimeLog
      // against whoever clicked, and there is no Atlas labour to time. Use
      // "Log Vendor Service" below instead.
      { label: 'Start Service',
        link: (row: any) => `/fleet/job-cards/${row.id}/service`,
        visible: (r) => r.status === 'active' && r.servicedWith !== 'Vendor' },
      { label: 'Log Past Service',
        link: (row: any) => `/fleet/job-cards/${row.id}/service?past=1`,
        visible: (r) => r.status === 'active' && r.servicedWith !== 'Vendor' },
      // Vendor jobs have no Atlas technician and no labour time of ours to
      // record, so they get their own entry point: fill Service Details
      // (the vendor's meter reading) + checklist, then submit straight to the
      // review queue. No timer, no assignee. Offered only for
      // servicedWith === 'Vendor'; the server enforces the same restriction.
      { label: 'Log Vendor Service',
        link: (row: any) => `/fleet/job-cards/${row.id}/service?vendor=1`,
        visible: (r) => r.status === 'active' && r.servicedWith === 'Vendor' },
      { label: 'View Service',
        link: (row: any) => `/fleet/job-cards/${row.id}/service`,
        visible: (r) => r.status !== 'active' },
      { label: 'Assign', endpoint: '/fleet/job-cards/:id/assign',
        actionKey: 'job-card.assign',
        pick: { endpoint: '/employees', refLabel: 'fullName', bodyKey: 'employeeIds', title: 'Assign technicians', multi: true },
        visible: (r) => r.status === 'active' && r.servicedWith !== 'Vendor' },
      // Vendor jobs are approvable straight from Active as well: there is no
      // technician submission to wait for, so a holder of job-card.approve can
      // sign one off as soon as the vendor confirms the work. Mirrors the
      // server's own precondition in /approve.
      { label: 'Approve', endpoint: '/fleet/job-cards/:id/approve',
        actionKey: 'job-card.approve',
        visible: (r) => r.status === 'completed'
          || (r.status === 'active' && r.servicedWith === 'Vendor') },
      { label: 'Reject', endpoint: '/fleet/job-cards/:id/reject',
        actionKey: 'job-card.reject',
        confirm: 'Reject this job card? This will void it.',
        visible: (r) => r.status === 'completed' },
      // Reverse transitions — only visible to roles granted the matching
      // action key. Used to undo a premature Approve/Reject or to send a
      // completed card back to the techs for amendment.
      { label: 'Revert to Completed', endpoint: '/fleet/job-cards/:id/revert-to-completed',
        actionKey: 'job-card.revert-to-completed',
        confirm: 'Revert this job card to Completed status? Review will be cleared.',
        visible: (r) => r.status === 'approved' || r.status === 'voided' },
      { label: 'Revert to Active', endpoint: '/fleet/job-cards/:id/revert-to-active',
        actionKey: 'job-card.revert-to-active',
        confirm: 'Revert this job card to Active? All submissions will be cleared.',
        visible: (r) => r.status === 'completed' },
    ],
    // `jobSheetNumber` is auto-generated server-side (atomic counter, see
    // server/src/models/job-card.model.ts). `jobSheetNumberDisplay` was
    // removed as a duplicate. `workflowStatus` defaults to 'active' on
    // the model (also enforced by `defaultValues` below) — operators
    // transition statuses via the rowActions (Submit / Approve / Reject
    // / Void) rather than editing the field directly. `totalTimeLogged`
    // is computed by aggregating TimeLog rows; editing it on the form
    // would just drift from reality.
    fields: [
      // Required: the post-save hook stamps this onto the ServiceHistory it
      // opens, and a card saved without it produces an orphaned service record
      // that no report and no next-due calculation can see. See `validate`.
      { name: 'asset', label: 'Asset', type: 'ref', required: true, refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      // Static multi-select for Zoho-parity job classifications.
      // Pick any combination — a single visit can be Scheduled
      // Service AND a WoF check.
      { name: 'jobType', label: 'Job Type', type: 'multi-select', required: true, options: [
        { value: 'Scheduled Service',       label: 'Scheduled Service' },
        { value: 'Unscheduled Maintenance', label: 'Unscheduled Maintenance' },
        { value: 'WoF or CoF',              label: 'WoF or CoF' },
      ] },
      // "Serviced With" steers the next field: 'Atlas' means we did the
      // work in-house and the operator picks a Service Location;
      // 'Vendor' means an external supplier did the work and the operator
      // picks a Vendor. Placed directly under Job Type (and above the
      // assignee) so the operator declares scope before drilling into
      // plan / schedule / assignment details.
      { name: 'servicedWith', label: 'Serviced With', type: 'select', options: [
        { value: 'Atlas',  label: 'Atlas' },
        { value: 'Vendor', label: 'Vendor' },
      ] },
      { name: 'serviceLocation', label: 'Service Location', type: 'ref',
        refEndpoint: '/master-data/service-locations', refLabel: 'name',
        visibleIf: (v) => v.servicedWith === 'Atlas' },
      { name: 'vendor', label: 'Vendor', type: 'ref',
        refEndpoint: '/master-data/vendors', refLabel: 'name',
        visibleIf: (v) => v.servicedWith === 'Vendor' },
      // Service Plan stays hidden until an Asset is picked — Plans are
      // scoped per asset (via `dependsOn`), so the dropdown would otherwise
      // surface an empty list and confuse the operator.
      // `autoSelectIfSingle` fills the field automatically when the
      // picked asset has exactly one plan — the operator never has to
      // click through a one-item dropdown.
      { name: 'servicePlan', label: 'Service Plan', type: 'ref', refEndpoint: '/servicing/plans', refLabel: 'name',
        dependsOn: { formField: 'asset', refField: 'assignedAssets' },
        visibleIf: (v) => !!v.asset,
        autoSelectIfSingle: true },
      // Schedule depends on Plan for the same reason.
      { name: 'servicePlanSchedule', label: 'Service Schedule', type: 'ref', refEndpoint: '/servicing/schedules', refLabel: 'name',
        dependsOn: { formField: 'servicePlan', refField: 'servicePlan' },
        visibleIf: (v) => !!v.servicePlan },
      { name: 'assignedTo', label: 'Assigned To', type: 'multi-ref', refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
      // Scheduled day — dispatchers can prep cards in advance, or
      // back-fill a date for a job that already happened. Distinct from
      // `startDate`/`endDate`, which are stamped by the server when the
      // technician starts/stops the timer (see fleet.routes.ts) and so
      // aren't part of the editable form.
      { name: 'jobDate', label: 'Scheduled Date', type: 'date' },
      { name: 'workDescription', label: 'Work Description', type: 'textarea' },
      // Review Notes are captured by the supervisor during approval — they
      // are not part of the initial create flow, so the field only renders
      // in edit mode (where the saved record carries an `id`).
      { name: 'reviewNotes', label: 'Review Notes', type: 'textarea',
        visibleIf: (v) => !!v.id },
    ],
    // The job card is where `servicePlanSchedule` originates: the post-save
    // hook on JobCard copies it onto the ServiceHistory it opens, and the
    // next-due compute can only advance a schedule it can see. A card saved
    // without one produces a service record that satisfies nothing, so the
    // asset reports overdue forever.
    //
    // Job Type is the gate. On prod it was empty on 5,472 of 6,281 cards, and
    // those cards produced 681 unlinked 2026 service records; cards that did
    // declare "Scheduled Service" were linked 518 of 538. Operators fill the
    // schedule in when asked and skip it when not, so requiring Job Type first
    // is what actually closes the leak. Mirrors jobCardSchema on the server.
    // The error is attached to the furthest-along field the operator can
    // actually SEE. Service Plan is hidden until an Asset is picked and the
    // Schedule until a Plan is, so hanging the message on `servicePlanSchedule`
    // when neither is visible would disable Create with nothing on screen
    // explaining why.
    // `v.id` is only set in edit mode. The Job Type requirement is create-only,
    // matching updateJobCardSchema on the server: 5,472 legacy cards carry an
    // empty jobType, and enforcing it on edit would block a supervisor from so
    // much as adding Review Notes to one. The Scheduled-Service → Schedule
    // pairing is enforced in both modes, because that is the rule that keeps
    // the next-due compute honest.
    validate: (v) => {
      const errs: Record<string, string> = {};
      // Create-only, like Job Type below: 18 legacy cards have no asset and
      // must stay editable. On create it is mandatory — a card without an
      // asset opens an orphaned ServiceHistory that nothing can later repair.
      // Empty string = "invalid, but say nothing". Both fields already carry
      // `required: true`, so the label shows the red asterisk and a second
      // sentence underneath was just noise. The key still has to be PRESENT:
      // `validate`'s key count is what disables Create, so returning nothing
      // at all here would let the form post and fail on the server instead.
      if (!v.asset && !v.id) errs.asset = '';
      const types: string[] = Array.isArray(v.jobType) ? v.jobType : [];
      if (types.length === 0) {
        if (!v.id) errs.jobType = '';
      } else if (types.includes('Scheduled Service') && !v.servicePlanSchedule) {
        if (!v.asset) errs.asset = 'Pick the asset, then its Service Plan and Schedule.';
        else if (!v.servicePlan) errs.servicePlan = 'Required for a Scheduled Service job card.';
        else errs.servicePlanSchedule = 'Required for a Scheduled Service job card.';
      }
      return Object.keys(errs).length ? errs : null;
    },
    defaultValues: { status: 'active' },
    // Create the card and sign it off in one click, for a holder of
    // job-card.approve. Requested so an approver recording work that is already
    // done doesn't have to create, submit and then approve their own card.
    //
    // What it SKIPS, deliberately, is the technician flow: no time logged, no
    // checklist, and no meter reading — `assertMeterReadingOk` guards /submit
    // and /submit-past, not /approve. Consequence on a non-IoT asset (nothing
    // else ever moves its meter): the approved service carries no reading, so
    // `resolveBaseline` anchors next-due on the CURRENT stored meter, which is
    // stale. Next due therefore lands one interval past a value the vehicle
    // passed some time ago, and the schedule drifts later with each such
    // approval. On an IoT asset the feed keeps the meter honest and the effect
    // does not arise. Date-based schedules are unaffected either way.
    //
    // The asset guard is not optional here: an approve is what stamps
    // ServiceHistory `Approved` and resets the schedule anchor, so a card with
    // no asset would record a service against nothing. `validate` above already
    // blocks Create for that, and the button is disabled independently below in
    // case the two ever diverge. The server enforces it a third time
    // (`assertAssetPresent`) — the only one that actually protects the data.
    createAndThen: {
      label: 'Create & Approve',
      pendingLabel: 'Creating & approving…',
      actionKey: 'job-card.approve',
      endpoint: '/fleet/job-cards/:id/approve',
      confirmTitle: 'Create and approve this job card?',
      // Plain string, deliberately one paragraph: this renders as the
      // description of the app's own modal (HTML), where "\n" collapses to a
      // space — it is not the browser's window.confirm any more.
      confirm: 'This skips the technician flow, so no labour time, checklist or meter reading '
        + 'is recorded. On an asset with no telematics, the missing reading pushes its next '
        + 'service due date later.',
      blockedReason: (v) => (v.asset ? null : 'Select the asset first — approving without one records the service against nothing.'),
    },
    // Materials live in their own collection (JobMaterial) referencing the
    // JobCard; the subform handles create-mode staging + edit-mode CRUD,
    // and the afterSave hook persists staged rows once the new card's id
    // is known.
    FormExtras: JobCardMaterialsSubform,
    afterSave: persistStagedJobMaterials,
    relatedLists: [
      { title: 'Time logs', endpoint: '/time-logs', filterField: 'jobCard',
        columns: [
          { key: 'employee.fullName', label: 'Employee', render: (r) => r.employee?.fullName || '—' },
          { key: 'startTime', label: 'Start', render: (r) => fmtDateTime(r.startTime) },
          { key: 'endTime', label: 'End', render: (r) => fmtDateTime(r.endTime, 'open') },
          { key: 'hours', label: 'Hours' },
        ] },
    ],
  },
};

// Archived + Disposed Assets — same column catalogue as Active. The server
// baseFilter on each endpoint enforces the right vehicleStatus subset
// regardless of any filter the user applies.
// Dedicated "Offboard Asset" entry-point — the log of offboarding requests
// submitted through the structured form (POST /assets/:id/offboard). Each
// row is one historical offboarding action, with the asset summary on the
// left and the audit fields (outcome / reason / who / when / IoT email
// status) on the right. To start a NEW offboarding, the user goes to the
// Assets report and right-clicks an active asset — the toolbar here just
// surfaces a shortcut to that.
FLEET_CONFIGS['offboard-asset'] = {
  title: 'Offboard Asset',
  singular: 'Offboarding',
  endpoint: '/fleet/offboardings',
  pageSlug: 'asset_offboarding_page',
  canCreate: false,
  canDelete: false,
  toolbarActions: [
    {
      label: 'Offboard a new asset',
      customDialog: AssetOffboardPickerDialog,
    },
  ],
  columns: [
    { key: 'fleetNumber', label: 'Fleet #', filterType: 'text' },
    { key: 'registrationNumber', label: 'Rego', filterType: 'text' },
    { key: 'make', label: 'Make', filterType: 'text' },
    { key: 'model', label: 'Model', filterType: 'text' },
    { key: 'vehicleStatus', label: 'Outcome', filterType: 'enum',
      enumOptions: [
        { value: 'Archived', label: 'Archived' },
        { value: 'Disposed', label: 'Disposed' },
      ],
      render: (r: any) => r.vehicleStatus || '—' },
    // Removal checklist. '—' is a real state here, not a formatting fallback:
    // offboardings recorded before these questions existed are unanswered,
    // and rendering that as "No" would assert the hardware was left in place.
    { key: 'gpsUnitRemoved', label: 'GPS Unit Removed', filterType: 'boolean',
      render: (r: any) => r.gpsUnitRemoved === true ? 'Yes' : r.gpsUnitRemoved === false ? 'No' : '—' },
    { key: 'fuelTagRemoved', label: 'Fuel Tag Removed', filterType: 'boolean',
      render: (r: any) => r.fuelTagRemoved === true ? 'Yes' : r.fuelTagRemoved === false ? 'No' : '—' },
    { key: 'offboardReason', label: 'Reason', filterType: 'text',
      render: (r: any) => r.offboardReason || '—' },
    { key: 'offboardAdditionalInfo', label: 'Additional Information', filterType: 'text',
      render: (r: any) => r.offboardAdditionalInfo || '—' },
    { key: 'offboardedBy.fullName', label: 'Offboarded By',
      filterType: 'ref', filterField: 'offboardedBy',
      refEndpoint: '/employees', refLabel: 'fullName',
      render: (r: any) => r.offboardedBy?.fullName || '—' },
    { key: 'offboardedAt', label: 'Date', filterType: 'date',
      render: (r: any) => fmtDateTime(r.offboardedAt) },
    { key: 'trackingProvider', label: 'IoT Provider', filterType: 'text',
      render: (r: any) => r.trackingProvider || '—' },
  ],
  // Read-only log — detail panel renders these fields when a row is opened.
  // No edit form is exposed (canCreate=false above + UI hides Edit on
  // resources without `edit` grants on the slug).
  fields: [
    { section: 'Asset', name: 'fleetNumber',         label: 'Fleet #',         type: 'text' },
    { section: 'Asset', name: 'registrationNumber',  label: 'Registration',    type: 'text' },
    { section: 'Asset', name: 'make',                label: 'Make',            type: 'text' },
    { section: 'Asset', name: 'model',               label: 'Model',           type: 'text' },
    { section: 'Asset', name: 'trackingProvider',    label: 'IoT Provider',    type: 'text' },
    { section: 'Offboarding', name: 'vehicleStatus', label: 'Outcome',         type: 'select', options: [
      { value: 'Archived', label: 'Archived' },
      { value: 'Disposed', label: 'Disposed' },
    ] },
    { section: 'Offboarding', name: 'offboardedAt',          label: 'Date',                  type: 'datetime' },
    { section: 'Offboarding', name: 'offboardedBy',          label: 'Offboarded By',         type: 'ref', refEndpoint: '/employees', refLabel: 'fullName' },
    { section: 'Offboarding', name: 'gpsUnitRemoved',        label: 'Have GPS unit been removed',  type: 'boolean' },
    { section: 'Offboarding', name: 'fuelTagRemoved',        label: 'Has Fuel Tag been removed',   type: 'boolean' },
    { section: 'Offboarding', name: 'offboardReason',        label: 'Reason',                type: 'text' },
    { section: 'Offboarding', name: 'offboardAdditionalInfo', label: 'Additional Information', type: 'textarea' },
  ],
};
FLEET_CONFIGS['archived-assets'] = {
  ...FLEET_CONFIGS.assets,
  title: 'Assets',
  singular: 'Archived Asset',
  endpoint: '/fleet/archived-assets',
  // Unified permission — the four asset buckets (Active / Archived /
  // Disposed / Contractor) all share `assets_page` view now. The three
  // legacy sub-slugs are dropped from the seed and stripped from roles.
  pageSlug: 'assets_page',
  toolbarActions: undefined,
  tabs: ASSET_LIFECYCLE_TABS,
  secondaryTabs: ASSET_IOT_TABS,
  // Records land here via the Archive bulk action on the active Assets
  // report — you can't create one directly.
  canCreate: false,
};
FLEET_CONFIGS['disposed-assets'] = {
  ...FLEET_CONFIGS.assets,
  title: 'Assets',
  singular: 'Disposed Asset',
  endpoint: '/fleet/disposed-assets',
  pageSlug: 'assets_page',
  toolbarActions: undefined,
  tabs: ASSET_LIFECYCLE_TABS,
  secondaryTabs: ASSET_IOT_TABS,
  // Records land here via the Dispose bulk action on the active Assets
  // report — you can't create one directly.
  canCreate: false,
};

// Contractor Vehicles — slim form, dispatch-only data. Same Asset
// collection underneath; the route's baseFilter scopes it by assetType.
FLEET_CONFIGS['contractor-vehicles'] = {
  title: 'Assets',
  singular: 'Contractor Vehicle',
  endpoint: '/fleet/contractor-vehicles',
  // Merged into the unified Assets permission — contractor view + add now
  // share the same role grants as the active/archived/disposed datasets.
  formSlug: 'assets',
  pageSlug: 'assets_page',
  tabs: ASSET_LIFECYCLE_TABS,
  secondaryTabs: ASSET_IOT_TABS,
  // Use the existing Asset transform on read but only surface the fields
  // a contractor entry actually needs.
  columns: [
    { key: 'fleetNumber', label: 'Fleet #', filterType: 'text', sortField: 'fleetNumberSort' },
    { key: 'registrationNumber', label: 'Rego', filterType: 'text' },
    { key: 'make', label: 'Make', filterType: 'text' },
    { key: 'model', label: 'Model', filterType: 'text' },
    { key: 'dispatchGroup.name', label: 'Dispatch Group',
      filterType: 'text', filterField: 'dispatchGroup',
      render: (r) => r.dispatchGroup?.name ?? '—' },
    { key: 'showInDispatch', label: 'Display in Scheduler', filterType: 'boolean',
      render: (r) => r.showInDispatch ? 'Yes' : 'No' },
    { key: 'vehicleStatus', label: 'Status', filterType: 'enum',
      enumOptions: [
        { value: 'Active', label: 'Active' },
        { value: 'Archived', label: 'Archived' },
      ] },
    { key: 'createdAt', label: 'Added', filterType: 'date',
      render: (r) => fmtDate(r.createdAt) },
  ],
  fields: [
    { name: 'fleetNumber', label: 'Fleet Number', type: 'text', required: true },
    { name: 'registrationNumber', label: 'Registration', type: 'text' },
    { name: 'make', label: 'Make', type: 'text' },
    { name: 'model', label: 'Model', type: 'text' },
    { name: 'year', label: 'Year', type: 'number' },
    { name: 'colour', label: 'Colour', type: 'text' },
    { name: 'dispatchGroup', label: 'Dispatch Group', type: 'ref',
      refEndpoint: '/master-data/dispatch-groups', refLabel: 'name' },
    { name: 'company', label: 'Company (owner)', type: 'ref',
      refEndpoint: '/master-data/companies', refLabel: 'name' },
    { name: 'showInDispatch', label: 'Display in Scheduler', type: 'boolean' },
    { name: 'vehicleStatus', label: 'Status', type: 'select', options: [
      { value: 'Active', label: 'Active' },
      { value: 'Archived', label: 'Archived' },
    ] },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],
  // The asset model defaults assetType to 'Atlas' — set 'Contractor' on
  // any record created from this surface so the new row is properly
  // scoped to /fleet/contractor-vehicles, not /fleet/assets.
  defaultValues: { assetType: 'Contractor', vehicleStatus: 'Active' },
  // assetType isn't an editable field, but we still need it in the submit
  // body. extraSaveKeys ensures formValues.assetType reaches the API even
  // though it doesn't appear in `fields`.
  extraSaveKeys: ['assetType'],
};

export const DISPATCH_CONFIGS: Record<string, CrudConfig> = {
  // eslint-disable-next-line no-empty-pattern
  // --- Dispatch
  jobs: {
    title: 'Dispatch Jobs', singular: 'Dispatch Job', endpoint: '/dispatch/jobs', formSlug: 'dispatch_jobs', pageSlug: 'dispatch_page', ownerField: 'driver',
    // Aligned with Zoho `All_Dispatch_Jobs.json`. Subform-derived columns
    // (product line counts) need a server join — Tier-B follow-up.
    columns: [
      { key: 'jobNumber', label: 'Job #', filterType: 'text' },
      { key: 'site.name', label: 'Site', filterType: 'ref', filterField: 'site',
        refEndpoint: '/master-data/sites', refLabel: 'name',
        render: (r) => r.site?.name || '—' },
      { key: 'customer', label: 'Customer', filterType: 'text' },
      { key: 'excludeFromWeightrax', label: 'Weightrax',
        render: (r) => (r.excludeFromWeightrax ? 'Excluded' : '—') },
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        refEndpoint: '/fleet/assets', refLabel: 'fleetNumber',
        render: (r) => r.asset?.fleetNumber || '—' },
      // Branch + vehicle group are derived from the linked Asset (server
      // nested-populates `asset.branch.name` and `asset.vehicleGroup.group`).
      // No `filterField` because the generic CRUD filter only accepts
      // top-level schema paths.
      { key: 'asset.branch.name', label: 'Branch',
        render: (r) => r.asset?.branch?.name || '—' },
      { key: 'asset.vehicleGroup.group', label: 'Vehicle Group',
        render: (r) => r.asset?.vehicleGroup?.group || '—' },
      { key: 'driver.fullName', label: 'Driver',
        filterType: 'text', filterField: 'driver',
        refEndpoint: '/employees', refLabel: 'fullName',
        render: (r) => r.driver?.fullName || '—' },
      { key: 'jobDetails', label: 'Job Details', filterType: 'text' },
      { key: 'phoneNumber', label: 'Phone', filterType: 'text' },
      { key: 'start', label: 'Start', filterType: 'date',
        render: (r) => fmtDateTime(r.start) },
      { key: 'end', label: 'End', filterType: 'date',
        render: (r) => fmtDateTime(r.end) },
      { key: 'productSummary.count', label: 'Products',
        render: (r) => r.productSummary?.count ?? 0 },
      { key: 'productSummary.quantity', label: 'Total Qty',
        render: (r) => typeof r.productSummary?.quantity === 'number'
          ? r.productSummary.quantity.toLocaleString()
          : '—' },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: SCHEDULED, label: 'Scheduled' },
          { value: 'En route', label: 'En route' },
          { value: 'Arrived', label: 'Arrived' },
          { value: 'Complete', label: 'Complete' },
          { value: 'Cancelled', label: 'Cancelled' },
        ] },
      // Status-transition timestamps + derived elapsed times. Server stamps
      // these in a pre-save hook; columns default-hidden, exposed for
      // dispatchers who run timing reports.
      { key: 'enRouteAt', label: 'En route at', filterType: 'date',
        render: (r) => fmtDateTime(r.enRouteAt) },
      { key: 'arrivedAt', label: 'Arrived at', filterType: 'date',
        render: (r) => fmtDateTime(r.arrivedAt) },
      { key: 'completedAt', label: 'Completed at', filterType: 'date',
        render: (r) => fmtDateTime(r.completedAt) },
      { key: 'driveTime', label: 'Drive time',
        render: (r) => fmtElapsedAdmin(r.enRouteAt, r.arrivedAt) },
      { key: 'jobTime', label: 'On-site time',
        render: (r) => fmtElapsedAdmin(r.arrivedAt, r.completedAt) },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
    ],
    // Status is set automatically to SCHEDULED on create and only flipped via the
    // "Mark Complete" row action — no manual edits.
    defaultValues: { status: SCHEDULED },
    fields: [
      { name: 'jobNumber', label: 'Job Number', type: 'text' },
      // Site / Customer Search / Other / read-only Customer name. Writes
      // `site`, `customerRef`, `customer` and (for Other) `newCustomer`; the
      // server creates the new customer in the same transaction as the job.
      { name: 'customerPicker', label: 'Customer', type: 'custom',
        render: ({ formValues, setField }) => React.createElement(CustomerPicker, {
          value: {
            excludeFromWeightrax: !!formValues.excludeFromWeightrax,
            site: formValues.site ?? null,
            customerRef: formValues.customerRef ?? null,
            customer: formValues.customer ?? null,
            newCustomer: formValues.newCustomer ?? null,
          },
          onChange: (v) => {
            setField('excludeFromWeightrax', v.excludeFromWeightrax);
            setField('site', v.site);
            setField('customerRef', v.customerRef);
            setField('customer', v.customer);
            // undefined, not null — the API rejects `newCustomer: null`.
            setField('newCustomer', v.newCustomer ?? undefined);
          },
        }) },
      // Saved from the picker above; hidden here so they can't be edited twice.
      // Still listed so they're submitted and shown on the detail panel / PDF.
      { name: 'excludeFromWeightrax', label: 'Exclude from Weightrax', type: 'boolean', visibleIf: () => false },
      { name: 'site', label: 'Site', type: 'ref', refEndpoint: '/master-data/sites', refLabel: 'name', visibleIf: () => false },
      { name: 'customer', label: 'Customer', type: 'text', visibleIf: () => false },
      // `fleetNumber` is a Zoho-era free-text column kept on the model
      // for legacy rows, but no current consumer reads it — every report,
      // dashboard, schedule grid and email template reaches through
      // `asset.fleetNumber` instead. Removed from the form so admins
      // don't accidentally type a string that drifts from the linked
      // Asset.
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'driver', label: 'Driver', type: 'ref', refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
      { name: 'jobDetails', label: 'Job Details', type: 'text' },
      { name: 'phoneNumber', label: 'Phone', type: 'text' },
      // start/end mirror the schedule-page drag dialog so a CRUD-created
      // job lands on the calendar without a follow-up edit.
      { name: 'start', label: 'Start', type: 'datetime' },
      { name: 'end', label: 'End', type: 'datetime' },
      // Single-line autocomplete (matches the Schedule drag-create
      // dialog). The structured address parts — line2/suburb, city,
      // region, postcode, country — are still cascaded into the saved
      // address object on selection; the user just doesn't see the
      // 6-field grid that master-data forms use.
      { name: 'address', label: 'Address', type: 'address-search' },
    ],
    rowActions: [
      { label: 'Mark Complete', endpoint: '/dispatch/jobs/:id', method: 'PUT',
        body: { status: 'Complete' },
        confirm: 'Mark this dispatch job as complete?',
        visible: (r) => r.status !== 'Complete' },
    ],
    FormExtras: DispatchJobProductsSubform,
    afterSave: persistStagedDispatchProducts,
    extraSaveKeys: ['customerRef', 'newCustomer'],
    validate: (v) => {
      const err = customerPickerError({
        excludeFromWeightrax: !!v.excludeFromWeightrax, site: v.site ?? null, customerRef: v.customerRef ?? null, customer: v.customer ?? null, newCustomer: v.newCustomer ?? null,
      });
      return err ? { newCustomer: err } : null;
    },
  },
  'technician-jobs': {
    title: 'Technician Jobs', singular: 'Technician Job', endpoint: '/dispatch/technician-jobs',
    // Full Zoho `All_Technician_Jobs.json` parity.
    columns: [
      { key: 'jobNumber', label: 'Job #', filterType: 'text' },
      { key: 'hiddenNumber', label: 'Hidden #', filterType: 'text' },
      { key: 'technicianName', label: 'Technician', filterType: 'text' },
      { key: 'technician.fullName', label: 'Engineer',
        filterType: 'text', filterField: 'technician',
        refEndpoint: '/employees', refLabel: 'fullName',
        render: (r) => r.technician?.fullName || '—' },
      { key: 'customer', label: 'Customer', filterType: 'text' },
      { key: 'jobDetails', label: 'Job Details', filterType: 'text' },
      { key: 'info', label: 'Info', filterType: 'text' },
      { key: 'start', label: 'Start', filterType: 'date',
        render: (r) => fmtDateTime(r.start) },
      { key: 'end', label: 'End', filterType: 'date',
        render: (r) => fmtDateTime(r.end) },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'New Job', label: 'New Job' },
          { value: 'Technician Assigned', label: 'Technician Assigned' },
          { value: 'Complete', label: 'Complete' },
        ] },
    ],
    fields: [
      { name: 'jobNumber', label: 'Job Number', type: 'text' },
      { name: 'technicianName', label: 'Technician', type: 'text' },
      { name: 'technician', label: 'Technician (Employee)', type: 'ref', refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
      { name: 'customer', label: 'Customer', type: 'text' },
      { name: 'jobDetails', label: 'Job Details', type: 'textarea' },
      { name: 'info', label: 'Info', type: 'textarea' },
      { name: 'status', label: 'Status', type: 'select', options: [
        { value: 'New Job', label: 'New Job' },
        { value: 'Technician Assigned', label: 'Technician Assigned' },
        { value: 'Complete', label: 'Complete' },
      ] },
    ],
  },
  'daily-activities': {
    title: 'Daily Activity', singular: 'Daily Activity', endpoint: '/dispatch/daily-activities',
    // Full Zoho `All_Daily_Activities.json` parity.
    columns: [
      { key: 'date', label: 'Date', filterType: 'date',
        render: (r) => fmtDate(r.date) },
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        refEndpoint: '/fleet/assets', refLabel: 'fleetNumber',
        render: (r) => r.asset?.fleetNumber || r.fleetNumber || '—' },
      { key: 'currentOdometer', label: 'Current Odometer', filterType: 'number' },
      { key: 'currentEngineHours', label: 'Current Engine Hours', filterType: 'number' },
      { key: 'distanceDriven', label: 'Distance Driven', filterType: 'number' },
      { key: 'engineHoursUtilised', label: 'Engine Hours Utilised', filterType: 'number' },
      { key: 'processed', label: 'Processed', filterType: 'boolean',
        render: (r) => r.processed ? 'Yes' : 'No' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
    ],
    fields: [
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'currentOdometer', label: 'Current Odometer', type: 'number' },
      { name: 'currentEngineHours', label: 'Current Engine Hours', type: 'number' },
      { name: 'distanceDriven', label: 'Distance Driven', type: 'number' },
      { name: 'engineHoursUtilised', label: 'Engine Hours Utilised', type: 'number' },
    ],
  },
};

export const SERVICING_CONFIGS: Record<string, CrudConfig> = {
  history: {
    title: 'Service History', singular: 'Service Record', endpoint: '/servicing/history', formSlug: 'service_history', pageSlug: 'services_page',
    // Edit on this report deep-links into the linked JobCard's service-
    // execution page (the same place "Start Service" goes). The dedicated
    // page carries the checklist UI / Start / Pause / Submit lifecycle —
    // the inline edit form on Service History was just a Zoho-era leftover
    // that didn't expose any of the workflow controls technicians need.
    editLink: (row: any) => {
      const jcId = row?.jobCard?.id ?? row?.jobCard?._id ?? row?.jobCard;
      return jcId ? `/fleet/job-cards/${jcId}/service` : null;
    },
    // Pre-filter tabs by workflow status (Draft / Approved / Rejected).
    // `status` is the unified field — `approvalStatus` was merged into it
    // so this is now the single source of truth.
    tabs: [
      { label: 'All' },
      { label: 'Draft',    filter: { status: 'Draft' } },
      { label: 'Approved', filter: { status: 'Approved' } },
      { label: 'Rejected', filter: { status: 'Rejected' } },
    ],
    // Aligned with Zoho `All_Service_Entries.json` / `Approved_Service_Entries.json`.
    // Aggregate columns (parts/labour totals from sub-collections) need a
    // server-side join — Tier-B follow-up.
    columns: [
      { key: 'jobSheetNumber', label: 'Job Sheet #', filterType: 'text' },
      { key: 'jobDate', label: 'Job Date', filterType: 'date',
        render: (r) => fmtDate(r.jobDate) },
      // asset and servicePlanSchedule filter by exact-ref so deep-links
      // (e.g. from the Service Report dashboard's per-cell click) land
      // pre-filtered to a single asset / schedule.
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'ref', filterField: 'asset',
        refEndpoint: '/fleet/assets', refLabel: 'fleetNumber',
        render: (r) => r.asset?.fleetNumber || r.fleetNumber || '—' },
      { key: 'fleetNumber', label: 'Fleet #', filterType: 'text' },
      { key: 'servicePlan.name', label: 'Service Plan',
        filterType: 'ref', filterField: 'servicePlan',
        refEndpoint: '/servicing/plans', refLabel: 'name',
        render: (r) => r.servicePlan?.name || '—' },
      // Text (with ref suggestions), not an exact-ref picker — matching Job
      // Cards. Every plan owns its own "A-Service" schedule, and records keep
      // the schedule id of whichever plan the asset was on at the time, so an
      // id-exact filter hides an asset's own history the moment it changes
      // plan. Filtering by name spans the 23 same-named schedules; combined
      // with the Asset filter that is exactly "this vehicle's A-Services".
      { key: 'servicePlanSchedule.name', label: 'Schedule',
        filterType: 'text', filterField: 'servicePlanSchedule',
        refEndpoint: '/servicing/schedules', refLabel: 'name',
        render: (r) => r.servicePlanSchedule?.name || '—' },
      { key: 'descriptionOfWork', label: 'Description', filterType: 'text' },
      { key: 'faultDescriptions', label: 'Fault Description', filterType: 'text' },
      { key: 'odometer', label: 'Odometer', filterType: 'number' },
      { key: 'engineHours', label: 'Engine Hours', filterType: 'number' },
      { key: 'hubo', label: 'Hubo', filterType: 'number' },
      { key: 'invoiceNumber', label: 'Invoice', filterType: 'text' },
      { key: 'timeLogSummary.hours', label: 'Time Logged',
        render: (r) => typeof r.timeLogSummary?.hours === 'number'
          ? r.timeLogSummary.hours.toLocaleString('en-GB', { maximumFractionDigits: 2 })
          : '—' },
      { key: 'checklistSummary.total', label: 'Checklist',
        render: (r) => r.checklistSummary
          ? `${r.checklistSummary.ok ?? 0} OK / ${r.checklistSummary.notOk ?? 0} Not OK / ${r.checklistSummary.total ?? 0}`
          : '—' },
      { key: 'documentCount', label: 'Documents', filterType: 'number',
        render: (r) => r.documentCount ?? 0 },
      { key: 'wofCof', label: 'WoF/CoF', filterType: 'enum',
        enumOptions: [{ value: 'WoF', label: 'WoF' }, { value: 'CoF', label: 'CoF' }] },
      { key: 'expiryDate', label: 'Expiry', filterType: 'date',
        render: (r) => fmtDate(r.expiryDate) },
      // Unified workflow status — replaces the prior split between a free-text
      // `status` ("Draft"/"Approved") and an enum `approvalStatus`. Single
      // source of truth.
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'Draft',    label: 'Draft' },
          { value: 'Approved', label: 'Approved' },
          { value: 'Rejected', label: 'Rejected' },
        ],
        render: (r) => r.status || 'Draft' },
      { key: 'servicedWith', label: 'Serviced With', filterType: 'enum',
        enumOptions: [
          { value: 'Atlas', label: 'Atlas' },
          { value: 'Vendor', label: 'Vendor' },
        ] },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
    ],
    bulkActions: [
      // `enabledFor` drops these when every selected record belongs to a job
      // card — the server skips those (`assertStandalone`), so without it the
      // action returns "0 approved, N skipped", which is correct but reads as
      // broken. Fail-open across pages; see the field's note in crud-page.
      { label: 'Approve selected', endpoint: '/servicing/history/bulk-approve',
        actionKey: 'service-history.bulk-approve',
        enabledFor: (r) => !r.jobCard,
        confirm: 'Approve all selected service records?' },
      { label: 'Reject selected', endpoint: '/servicing/history/bulk-reject',
        actionKey: 'service-history.bulk-reject',
        enabledFor: (r) => !r.jobCard,
        promptReason: true, variant: 'destructive' },
    ],
    // Orphaned Zoho-imported ServiceHistory rows (no linked Asset) are
    // hidden unconditionally — see the route's defaultFilter in
    // server/src/routes/servicing.routes.ts. No user-facing toggle.
    rowActions: [
      // "Start Service" / "View Service" were removed — the standard Edit
      // (pen icon / context menu / detail panel) deep-links to the same
      // JobCard service-execution page via `editLink` above. One entry
      // point keeps the report cleaner.
      // STANDALONE records only — the server enforces it (`assertStandalone`).
      // A record created by a job card is approved THROUGH that card, which
      // also reconciles the schedule anchor, fans out any `includes` siblings
      // and stamps the card's own review fields; approving it here would do
      // none of that and leave the two disagreeing.
      //
      // Shown-but-blocked rather than hidden. Hiding it meant a user saw
      // "Approve selected" in the toolbar (bulk actions carry no per-row
      // predicate) while the per-row Approve was simply absent, which reads as
      // a bug or a missing permission. `blocked` explains the rule and offers
      // the job card, which is where the action actually lives. The list
      // populates `jobCard` with `status jobSheetNumber`, so both the message
      // and the link have what they need.
      { label: 'Approve', endpoint: '/servicing/history/:id/approve',
        actionKey: 'service-history.approve',
        visible: (r) => r.status !== 'Approved',
        blocked: (r) => (r.jobCard ? {
          title: 'Approve this from its job card',
          message: `This service record belongs to job card ${r.jobCard.jobSheetNumber ?? ''}`
            + `${r.jobCard.status ? ` (${r.jobCard.status})` : ''}. Approving it there also updates the`
            + ' job card, re-anchors the service schedule and covers any schedules the plan includes —'
            + ' none of which happens if the record is approved on its own.',
          goTo: `/fleet/job-cards/${r.jobCard.id ?? r.jobCard._id}/service`,
          goToLabel: 'Open job card',
        } : null) },
      { label: 'Reject', endpoint: '/servicing/history/:id/reject',
        actionKey: 'service-history.reject',
        promptReason: true, variant: 'destructive',
        visible: (r) => r.status !== 'Rejected',
        blocked: (r) => (r.jobCard ? {
          title: 'Reject this from its job card',
          message: `This service record belongs to job card ${r.jobCard.jobSheetNumber ?? ''}`
            + `${r.jobCard.status ? ` (${r.jobCard.status})` : ''}. Reject it there so the card and the`
            + ' record cannot end up disagreeing.',
          goTo: `/fleet/job-cards/${r.jobCard.id ?? r.jobCard._id}/service`,
          goToLabel: 'Open job card',
        } : null) },
    ],
    // Field order + conditional visibility mirror the Zoho Service_History
    // form so users moving over from Zoho see a familiar layout.
    //   1. `Serviced With` is on top because it gates Vendor vs Atlas
    //      Service Location below it.
    //   2. `Service Type` gates Service Plan/Schedule (Scheduled Service)
    //      and WoF/CoF + Expiry (WoF or CoF).
    //   3. Costs live in their own section on the right.
    fields: [
      // ── Service Source ─────────────────────────────────────────────
      { section: 'Service Source', name: 'servicedWith', label: 'Serviced With', type: 'select', options: [
        { value: 'Atlas',  label: 'Atlas' },
        { value: 'Vendor', label: 'Vendor' },
      ] },
      // Shown only when Serviced With = Vendor.
      { section: 'Service Source', name: 'vendor', label: 'Vendor', type: 'ref',
        refEndpoint: '/master-data/vendors', refLabel: 'name',
        visibleIf: (v) => v.servicedWith === 'Vendor' },
      // Shown only when Serviced With = Atlas.
      { section: 'Service Source', name: 'serviceLocation', label: 'Atlas Service Location', type: 'ref',
        refEndpoint: '/master-data/service-locations', refLabel: 'name',
        visibleIf: (v) => v.servicedWith === 'Atlas' },
      { section: 'Service Source', name: 'invoiceNumber', label: 'Invoice Number', type: 'text' },
      { section: 'Service Source', name: 'jobSheetNumber', label: 'Job Sheet Number', type: 'text' },

      // ── Job ────────────────────────────────────────────────────────
      // Single Asset picker — `fleetNumber` on the record is auto-stamped by
      // the model pre-save hook from the linked asset, so the user only
      // needs to pick the asset once.
      { section: 'Job', name: 'asset', label: 'Asset', type: 'ref',
        refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      // Status is workflow-driven, NOT a user field on the form:
      //   • New record → defaults to "Draft" via the model.
      //   • Tech completes the service → status moves to "Completed" via
      //     the JobCard service-execution page.
      //   • Supervisor uses the Approve / Reject row actions on the
      //     Service History report → status moves to Approved / Rejected.
      // Status still renders as a column on the report so it's visible
      // for filtering and audit; it just isn't on the Log Service form.
      // Required. `serviceType` gates the Service Plan / Schedule fields
      // below, so leaving it blank hides them entirely — which is exactly how
      // prod ended up with `serviceType: null` on 22,461 of 22,492 records and
      // no schedule link on 13,686 of them. See the `validate` hook below.
      { section: 'Job', name: 'serviceType', label: 'Service Type', type: 'select', required: true, options: [
        { value: 'Scheduled Service',        label: 'Scheduled Service' },
        { value: 'Unscheduled Maintenance',  label: 'Unscheduled Maintenance' },
        { value: 'WoF or CoF',               label: 'WoF or CoF' },
      ] },
      // WoF/CoF and its expiry date appear only when the service is a
      // compliance check — mirrors Script_Always_Run2 in Zoho.
      { section: 'Job', name: 'wofCof', label: 'WoF/CoF', type: 'select', options: [
        { value: 'WoF', label: 'WoF' },
        { value: 'CoF', label: 'CoF' },
      ], visibleIf: (v) => v.serviceType === 'WoF or CoF' },
      { section: 'Job', name: 'expiryDate', label: 'WoF/CoF Expiry Date', type: 'date',
        visibleIf: (v) => v.serviceType === 'WoF or CoF' },
      // Service Plan + Schedule only relevant for the Scheduled Service
      // path. Schedule cascades from the chosen plan via `dependsOn` so
      // the dropdown stays filtered to that plan's schedules. The plan
      // itself cascades from the picked asset (only the plans the asset
      // is assigned to are shown), and auto-selects when there's exactly
      // one match — matches the Job Card form behaviour.
      { section: 'Job', name: 'servicePlan', label: 'Service Plan', type: 'ref',
        refEndpoint: '/servicing/plans', refLabel: 'name',
        dependsOn: { formField: 'asset', refField: 'assignedAssets' },
        autoSelectIfSingle: true,
        visibleIf: (v) => v.serviceType === 'Scheduled Service' },
      { section: 'Job', name: 'servicePlanSchedule', label: 'Service Plan Schedule', type: 'ref',
        refEndpoint: '/servicing/schedules', refLabel: 'name',
        dependsOn: { formField: 'servicePlan', refField: 'servicePlan' },
        visibleIf: (v) => v.serviceType === 'Scheduled Service' },
      { section: 'Job', name: 'jobDate', label: 'Job Date', type: 'date' },
      { section: 'Job', name: 'odometer', label: 'Speedo', type: 'number' },
      { section: 'Job', name: 'hubo', label: 'Hubo', type: 'number' },
      { section: 'Job', name: 'engineHours', label: 'Engine Hours', type: 'number' },
      { section: 'Job', name: 'nextOilChangeMileage', label: 'Next Oil Change Mileage', type: 'number' },

      // ── Work Details ───────────────────────────────────────────────
      { section: 'Work Details', name: 'servicedBy', label: 'Serviced By', type: 'ref',
        refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
      { section: 'Work Details', name: 'descriptionOfWork', label: 'Description of Work', type: 'textarea' },
      { section: 'Work Details', name: 'faultDescriptions', label: 'Fault Descriptions', type: 'textarea' },
      // Job Code field removed — operators don't track per-record job codes
      // on this form anymore. Invoice Details section (labor / parts / tax /
      // total) also removed: those costs live in the Labour Costs and
      // Material Costs related lists, where they're broken out per line
      // item rather than rolled into form inputs. Hiding them here keeps
      // the Log Service flow focused on the service activity itself.
    ],
    // A service record with no `servicePlanSchedule` is invisible to the
    // next-due compute — it satisfies no schedule, so the asset keeps
    // reporting overdue however much work is logged against it. Requiring
    // Service Type first is what makes the Plan / Schedule fields appear at
    // all; without it they stay hidden and the record saves unlinked.
    //
    // This form is the minor path (the Job Card form originates most service
    // records — see the 'job-cards' config), but it has the same hole.
    // Create-only on Service Type, for the same reason as the job-card form:
    // 22,461 legacy records have it null and editing one must not be blocked.
    validate: (v) => {
      const errs: Record<string, string> = {};
      if (!v.serviceType) {
        if (!v.id) errs.serviceType = 'Select a Service Type.';
      } else if (v.serviceType === 'Scheduled Service' && !v.servicePlanSchedule) {
        // Same visible-field chaining as the job-card form: Plan and Schedule
        // both hide until the field before them has a value.
        if (!v.asset) errs.asset = 'Pick the asset, then its Service Plan and Schedule.';
        else if (!v.servicePlan) errs.servicePlan = 'Required for a Scheduled Service record.';
        else errs.servicePlanSchedule = 'Required for a Scheduled Service record.';
      }
      return Object.keys(errs).length ? errs : null;
    },
    FormExtras: ServiceHistoryDocumentsSubform,
    relatedLists: [
      { title: 'Labour costs', endpoint: '/service-labour-costs', filterField: 'serviceHistory',
        columns: [
          { key: 'employee.fullName', label: 'Employee', render: (r) => r.employee?.fullName || '—' },
          { key: 'hours', label: 'Hours' },
          { key: 'cost', label: 'Cost' },
        ] },
      // Material costs — hidden 2026-08-14. It can never populate: the section
      // filters on `serviceHistory`, but that link does not exist in the Zoho
      // source. The export ships `basic#Service_Materials_Costs.csv` with only
      // Added_Time / Added_User / Cost / Description / ID / Modified_* /
      // Record_Status / ZC_Record_Type — no parent column and no `rel#` file,
      // unlike labour costs which has `rel#Labour_Cost#Service_History.csv`.
      // Hence serviceHistory is null on all 7 rows on BOTH dev and prod; there
      // was nothing to import, so this is not a recoverable orphan.
      //
      // Those 7 rows are also test data ("John" $60, "34534" $50) against
      // 22,308 service records. Materials in real use are tracked via
      // `jobmaterials` and the Material catalogue, reached from job cards.
      // { title: 'Material costs', endpoint: '/service-material-costs', filterField: 'serviceHistory',
      //   columns: [
      //     { key: 'description', label: 'Description' },
      //     { key: 'cost', label: 'Cost' },
      //   ] },
      { title: 'Documents', endpoint: '/service-history-documents', filterField: 'serviceHistory',
        columns: [
          { key: 'documentName', label: 'Name' },
          { key: 'type', label: 'Type' },
        ] },
      { title: 'Time logs', endpoint: '/time-logs', filterField: 'serviceHistory',
        columns: [
          { key: 'employee.fullName', label: 'Employee', render: (r) => r.employee?.fullName || '—' },
          { key: 'hours', label: 'Hours' },
        ] },
      { title: 'Checklist answers', endpoint: '/service-history-checklist-answers', filterField: 'serviceHistory',
        columns: [
          { key: 'task', label: 'Task' },
          { key: 'answer', label: 'Answer' },
          { key: 'comments', label: 'Comments' },
        ] },
      { title: 'Change history', endpoint: '/audit-logs', filterField: 'entityId',
        columns: [
          { key: 'createdAt', label: 'When', render: (r) => fmtDateTime(r.createdAt) },
          { key: 'operation', label: 'Op' },
          { key: 'changedBy.fullName', label: 'By', render: (r) => r.changedBy?.fullName || r.source },
          { key: 'changes', label: 'Changes', render: (r) => (r.changes || []).map((c: any) => c.field).join(', ') || '—' },
        ] },
    ],
  },
  plans: {
    title: 'Service Plans', singular: 'Service Plan', endpoint: '/servicing/plans', formSlug: 'service_plans', pageSlug: 'service_plans_page',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'assetCount', label: 'Assets', render: (r) => r.assignedAssets?.length ?? 0 },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
    ],
    fields: [{ name: 'name', label: 'Name', type: 'text', required: true }],
    // Create-mode subform: lets the operator stage schedules + their
    // checklist items inline while adding the plan. afterSave walks the
    // staged list once the plan id exists and POSTs each schedule (+
    // its checklist parent + items) sequentially. Edit-mode keeps the
    // existing relatedLists "Edit Checklist" flow — the subform renders
    // nothing in that case.
    FormExtras: ServicePlanScheduleSubform,
    afterSave: persistStagedServicePlanSchedules,
    // Surface related lists in the edit form too — schedules are the core
    // context for "what does this plan do", so operators expect to see /
    // tweak them while editing the plan, not just while viewing it.
    showRelatedListsInEdit: true,
    relatedLists: [
      { title: 'Schedules in this plan', endpoint: '/servicing/schedules', filterField: 'servicePlan',
        columns: [
          { key: 'name', label: 'Name' },
          { key: 'serviceInterval', label: 'Interval' },
          { key: 'unitOfMeasurement', label: 'Unit' },
          { key: 'recurring', label: 'Recurring', render: (r) => r.recurring ? 'Yes' : 'No' },
          // "Edit Checklist" opens the unified dialog (schedule metadata
          // + checklist items in one place). Click bubbles are stopped
          // inside the link so the surrounding row-click handler
          // (which opens the lightweight rowEdit dialog) doesn't fight
          // for the same click.
          { key: '_edit', label: '',
            render: (r) => React.createElement(EditChecklistLink, {
              scheduleId: r.id,
              scheduleName: r.name,
              servicePlanId: r.servicePlan?.id ?? r.servicePlan,
            }) },
        ],
        // `rowEditFields` here drive the "+ Add" dialog only — row-click
        // edit is intentionally disabled so the Edit link in the rightmost
        // column is the single edit affordance (opens the unified
        // schedule + checklist editor). Having both was confusing because
        // the row-click dialog hid the checklist while the link dialog
        // exposed it.
        rowEditFields: [
          { name: 'name', label: 'Name', type: 'text', required: true },
          { name: 'serviceInterval', label: 'Service Interval', type: 'number' },
          { name: 'unitOfMeasurement', label: 'Unit of Measurement', type: 'select', options: [
            { value: 'Distance (km)',      label: 'Distance (km)' },
            { value: 'Hours of Operation', label: 'Hours of Operation' },
            { value: 'Days',               label: 'Days' },
            { value: 'Months',             label: 'Months' },
          ] },
          { name: 'recurring', label: 'Recurring', type: 'boolean' },
          // Setting `isOilChange` explicitly trips the model's
          // `isOilChangeAuto` flag to false (post-save hook), so the
          // name heuristic won't overwrite the operator's pick later.
          { name: 'isOilChange', label: 'Engine Oil Change', type: 'boolean' },
          // Includes scoped to the parent plan. The row-edit dialog
          // seeds `servicePlan` from the related-list's `filterField`
          // (see crud-page.tsx `openRowCreate`), so `dependsOn` has
          // something to anchor to even on create.
          { name: 'includes', label: 'Include', type: 'multi-ref',
            refEndpoint: '/servicing/schedules', refLabel: 'name',
            dependsOn: { formField: 'servicePlan', refField: 'servicePlan' } },
        ],
        rowEditSingular: 'Schedule',
        rowCreate: true,
        disableRowClickEdit: true,
      },
      { title: 'Service history on this plan', endpoint: '/servicing/history', filterField: 'servicePlan',
        // History is reference / audit context — useful on the detail
        // view but pure noise while editing the plan itself.
        hideInEdit: true,
        columns: [
          { key: 'jobSheetNumber', label: 'Job #' },
          { key: 'jobDate', label: 'Date', render: (r) => fmtDate(r.jobDate) },
          // ServiceHistory stores the linked vehicle as `asset` (the legacy
          // Zoho `vehicle` field name was a translation artefact); reading
          // `r.vehicle` was always undefined which is why the column rendered
          // blank. `total` column dropped — operators don't need it in this
          // context, the full row is one click away on Service History.
          { key: 'asset.fleetNumber', label: 'Asset', render: (r) => r.asset?.fleetNumber || '—' },
        ] },
      // "Schedule checklists" related list removed — checklist editing is
      // now reached via the "Edit Checklist" button on each schedule row
      // above (one click, opens the editor dialog), so a separate list of
      // checklist join-rows is redundant.
    ],
  },
  schedules: {
    title: 'Service Schedules', singular: 'Schedule', endpoint: '/servicing/schedules', formSlug: 'service_plan_schedules', pageSlug: 'service_plan_schedules_page',
    // Full Zoho `All_Service_Plan_Tasks.json` parity.
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'servicePlan.name', label: 'Service Plan',
        filterType: 'text', filterField: 'servicePlan',
        render: (r) => r.servicePlan?.name || '—' },
      { key: 'serviceInterval', label: 'Service Interval', filterType: 'number' },
      { key: 'unitOfMeasurement', label: 'Unit of Measurement', filterType: 'text' },
      { key: 'recurring', label: 'Recurring', filterType: 'boolean',
        render: (r) => (r.recurring ? 'Yes' : 'No') },
      { key: 'isOilChange', label: 'Engine Oil', filterType: 'boolean',
        render: (r) => (r.isOilChange ? 'Yes' : '—') },
      { key: 'condition', label: 'Condition', filterType: 'text' },
      { key: 'formula', label: 'Formula', filterType: 'text' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
      // Same "Edit Checklist" link the Service Plan detail view offers —
      // gives operators a direct way to manage checklist items + reorder
      // them from the report without first drilling into the parent plan.
      { key: '_edit', label: '',
        render: (r) => React.createElement(EditChecklistLink, {
          scheduleId: r.id,
          scheduleName: r.name,
          servicePlanId: r.servicePlan?.id ?? r.servicePlan,
        }) },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'servicePlan', label: 'Service Plan', type: 'ref', refEndpoint: '/servicing/plans', refLabel: 'name' },
      { name: 'recurring', label: 'Recurring', type: 'boolean' },
      { name: 'serviceInterval', label: 'Service Interval', type: 'number' },
      // Constrained to the same options the checklist-editor dialog uses
      // so the downstream Service Report / upcoming-services compute can
      // rely on a known set rather than parsing free text.
      { name: 'unitOfMeasurement', label: 'Unit of Measurement', type: 'select', options: [
        { value: 'Distance (km)',      label: 'Distance (km)' },
        { value: 'Hours of Operation', label: 'Hours of Operation' },
        { value: 'Days',               label: 'Days' },
        { value: 'Months',             label: 'Months' },
      ] },
      // `formula` was a Zoho-side computed field (literal expression
      // `input.Service_Plan`) that just mirrored the parent ref. Nothing
      // in this codebase reads it for business logic, so it doesn't
      // belong on the form. The column on the report keeps the migrated
      // value visible for audit; you just can't edit it here anymore.
      // Marks this schedule as the engine-oil row for the Service Report's
      // "Engine Oil Due" column. Auto-populated from the schedule name on
      // create/save unless the operator has manually flipped it here.
      { name: 'isOilChange', label: 'Engine oil change', type: 'boolean' },
      // Schedule includes — when this schedule is performed, the schedules
      // listed here are also considered satisfied (sibling ServiceHistory
      // rows are stamped on approval). `dependsOn` restricts the picker to
      // schedules on the *same* service plan; the server validator enforces
      // that rule too, so a stale form can't sneak a cross-plan include
      // through. Self-include + cycles are rejected server-side.
      { name: 'includes', label: 'Include', type: 'multi-ref',
        refEndpoint: '/servicing/schedules', refLabel: 'name',
        dependsOn: { formField: 'servicePlan', refField: 'servicePlan' },
        visibleIf: (v) => !!v.servicePlan },
    ],
    relatedLists: [
      { title: 'Service history on this schedule', endpoint: '/servicing/history', filterField: 'servicePlanSchedule',
        columns: [
          { key: 'jobSheetNumber', label: 'Job #' },
          { key: 'jobDate', label: 'Date', render: (r) => fmtDate(r.jobDate) },
          // ServiceHistory stores the linked vehicle as `asset` (legacy Zoho
          // `vehicle` field was a translation artefact); reading `r.vehicle`
          // is always undefined.
          { key: 'asset.fleetNumber', label: 'Vehicle', render: (r) => r.asset?.fleetNumber || '—' },
        ] },
      { title: 'Upcoming services', endpoint: '/upcoming-services', filterField: 'servicePlanSchedule',
        columns: [
          { key: 'asset.fleetNumber', label: 'Vehicle', render: (r) => r.asset?.fleetNumber || '—' },
          { key: 'nextServiceDate', label: 'Next Date', render: (r) => fmtDate(r.nextServiceDate) },
        ] },
      // Ordered checklist of tasks the technician works through for this
      // schedule. Resolved server-side via the virtual `servicePlanSchedule`
      // filter on the items route (translates to `parent $in` against the
      // schedule's checklist join rows).
      { title: 'Checklist items for this schedule', endpoint: '/service-plan-schedule-checklist-items', filterField: 'servicePlanSchedule',
        filterExtra: { sort: 'sortOrder', order: 'asc' },
        columns: [
          { key: 'sortOrder', label: '#' },
          { key: 'groupHeader', label: 'Group', render: (r) => r.groupHeader || '—' },
          { key: 'task', label: 'Task', render: (r) => r.task || '—' },
        ] },
    ],
  },
};
